// https://stackoverflow.com/q/73885230
//createWebHashHistory // createMemoryHistory
import {getPosterAddress} from "@/composables/usePosters.js";
import {noteYearArrival} from "@/composables/useYearArrival.js";
import {createRouter, createWebHistory} from "vue-router";
import history from "@/assets/history.json";
import {legacyBlogRedirects} from "./legacyBlogRedirects";
import {FILM_PATHS} from "@/components/Video/films";
import {getCurrentNewsYear} from "@/composables/useNews";
import {getFullstackUrl} from "@/composables/useFullstackUrl";
import {getUrlWithAuth} from "@/composables/useUrlWithAuth";
import {storePrefillEmail, takeHashEmail} from "@/composables/prefillEmail";
import {signInWithMagic, takeHashMagic} from "@/composables/magicLink";
import {getIsSidebarHash} from "@/composables/useYearSidebar";
const log = (msg) => process.env.VUE_APP_DEV && console.log(); // eslint-disable-line

const redirectToCurrentYear = (to) => {
  log(`router: origin = ${window.location.origin}`);
  log(`router: latestYear = ${history.latestYear}`);
  log(`router: to.path = ${to.path}`);
  const url = new URL(window.location.href);
  // Construct new URL with the latestYear and existing query/hash
  const newUrl = `${window.location.origin}/${history.latestYear}${to.path}${url.search}${url.hash}`;
  log(`router: redirecting to ${newUrl}`);
  window.location = newUrl;
};

log(`routes/index.js: redirectToCurrentYear = ${redirectToCurrentYear}`);

// {
//   path: "/:pathMatch(.*)*",
//   beforeEnter: (to, from, next) => {
//     log("router: catch-all route triggered");
//     if (to.path.match(/^\/\d{4}/)) {
//       next();
//       return;
//     }
//     redirectToCurrentYear(to);
//   }
//   // redirect: "/"
// },

// a poster's number as the database has it, however typed: "a09" → "A09", "VC16" → "vC16"
const getPosterNum = (text) =>
  text.replace(/^(v?)(.)/i, (all, virtual, session) => {
    return `${virtual.toLowerCase()}${session.toUpperCase()}`;
  });

// the years with a registration form of their own (2024, 2025); any other year's address goes
// to its Registration page (2026's Swoogo form removed, 2026-10-30)
const formFiles = import.meta.webpackContext("../assets/years", {
  recursive: true,
  regExp: /Registration\/Form\/Index\.vue$/,
  mode: "lazy"
});
const redirectWithoutForm = (to) => {
  const formFile = `./${to.params.year}/Registration/Form/Index.vue`;
  if (!formFiles.keys().includes(formFile)) return `/${to.params.year}/Registration`;
};

const routes = [
  // {path: "/", beforeEnter: redirectToCurrentYear},
  // redirect everything else to the current year / home
  {
    path: "/",
    name: "Root",
    // the query too: a link from the fullstack carries the Clerk session there on staging and
    // local (?__clerk_db_jwt=…, getUrlWithAuth); dropping it signed the visitor out
    beforeEnter: () => {
      const {origin, search, hash} = window.location;
      window.location = `${origin}/${history.latestYear}${search}${hash}`;
    }
  },
  {path: "/Masterclass", name: "StaticMasterclass", beforeEnter: redirectToCurrentYear},
  {path: "/Program", name: "StaticProgram", beforeEnter: redirectToCurrentYear},
  {path: "/Registration", name: "StaticRegistration", beforeEnter: redirectToCurrentYear},
  {path: "/Registration/Form", name: "StaticRegistrationForm", beforeEnter: redirectToCurrentYear},
  {path: "/People", name: "StaticPeople", beforeEnter: redirectToCurrentYear},
  {path: "/Contact", name: "StaticContact", beforeEnter: redirectToCurrentYear},
  {path: "/Venue", name: "StaticVenue", beforeEnter: redirectToCurrentYear},
  {path: "/Sponsors", name: "StaticSponsors", beforeEnter: redirectToCurrentYear},
  {
    path: "/Showcases",
    name: "Showcases",
    component: () => import("../views/Showcases.vue")
  },
  {path: "/Showcase", redirect: "/Showcases"},
  {
    path: "/register",
    name: "Register",
    beforeEnter: () => {
      const base = getFullstackUrl() || "https://fullstack.vizbi.org";
      window.location.replace(getUrlWithAuth(`${base}/#register`));
    }
  },
  {
    // the About page is the one-pager (Seán, 2026-11-05; was views/About.vue): a standalone page,
    // outside the site's layout (App.vue), its PDF rendered from it by the fullstack
    // (api/v1/one-pager/pdf, which opens /one-pager?pdf)
    path: "/About",
    alias: "/one-pager",
    name: "About",
    meta: {isStandalone: true},
    component: () => import("../views/OnePager.vue")
  },
  {path: "/onepager", redirect: "/About"},
  // the old WordPress blog's URLs, now in /news
  ...legacyBlogRedirects,
  {
    // a poster's own short address, to cite it by (PosterPage.vue): /poster/2025/A09 →
    // /Posters/2025/A09
    path: "/poster/:year(\\d{4})/:posterNum(v?[A-Za-z]\\d\\d)",
    redirect: ({params, hash}) => {
      return {path: `/Posters/${params.year}/${getPosterNum(params.posterNum)}`, hash};
    }
  },
  {
    path: "/Posters/:specification*",
    name: "Posters",
    component: () => import("../views/Posters/Index.vue"),
    children: [
      {
        path: "Upload",
        name: "Upload",
        component: () => import("../views/Posters/Upload.vue")
      }
    ]
  },
  {
    path: "/Breakout/:specification*",
    name: "Breakout",
    beforeEnter(to) {
      window.location.href = `https://www.vizbi.org${to.path}`;
    }
  },
  {
    path: "/Masterclass/Upload/:specification*",
    name: "MasterclassUpload",
    beforeEnter(to) {
      window.location.href = `https://www.vizbi.org${to.path}`;
    }
  },
  {
    // an online event's feedback, also reached from the follow-up email (?event=masterclass-2026;
    // Seán, 2026-11-03); was a redirect to an old Google Form
    path: "/feedback",
    name: "Feedback",
    component: () => import("../views/Feedback.vue")
  },
  {
    // the poster gallery's Art & Biology (the old server's ?category=Art until 2026-10-27)
    path: "/art",
    name: "Art",
    redirect: {path: "/Posters", query: {category: "Art & Biology"}}
  },
  {
    path: "/zoom",
    name: "Zoom",
    beforeEnter() {
      // Online Masterclass: keep for crew only
      window.location.href = `https://us06web.zoom.us/j/84820156897?pwd=P81e0spJrqfvnAjvWG5E3G6CC0tt6c.1`;
      // 'personal meeting room'
      // window.location.href = `https://us06web.zoom.us/j/3541211533?pwd=Z2VRUkVpSFJYaTI3bXhGL0YrejNZUT09`;
    }
  },
  {
    path: "/online",
    name: "Online",
    beforeEnter() {
      // A synonym for Online Masterclass: for all attendees
      window.location.href = `https://us06web.zoom.us/j/84820156897?pwd=P81e0spJrqfvnAjvWG5E3G6CC0tt6c.1`;
    }
  },
  {
    // the year is always in the path
    path: "/news",
    name: "News",
    redirect: () => `/news/${getCurrentNewsYear()}`
  },
  {
    path: "/news/:year(\\d{4})",
    name: "News Year",
    component: () => import("../views/News/Index.vue")
  },
  {
    // /news/2026/04-30; /news/2026/04-30-b for a second item on that date (no parentheses in a
    // vue-router param pattern: it can't parse them, hence the alternation)
    path: "/news/:year(\\d{4})/:day(\\d{2}-\\d{2}|\\d{2}-\\d{2}-[a-z])",
    name: "News Item",
    component: () => import("../views/News/Item.vue")
  },
  {
    path: "/collective/:videoId([\\w-]{11}|\\d+)?",
    name: "Collective",
    component: () => import("../views/Collective.vue"),
    meta: {isSamePageAcrossParams: true} // a video ID keeps this page and its scroll
  },
  {
    path: "/certificate",
    name: "Certificate",
    beforeEnter() {
      window.location.href = `https://www.iscb.org/cms_addon/conferences/vizbi2025/easychair/certificate`;
    }
  },
  {
    path: "/Lightning/:specification*",
    name: "Lightning",
    beforeEnter(to) {
      window.location.href = `https://www.vizbi.org${to.path}`;
    }
  },
  {
    path: "/Photos",
    name: "Photos",
    component: () => import("../views/Photos.vue"),
    children: [{path: ":photoYear(20\\d\\d)"}]
  },
  {
    path: "/Plus",
    component: () => import("../views/Plus/Index.vue"),
    children: [
      {
        path: ":videoId([\\w-]{11}|\\d+)?",
        name: "VizbiPlus",
        component: () => import("../views/Plus/Home.vue"),
        meta: {isSamePageAcrossParams: true} // a video ID keeps this page and its scroll
      },
      {path: "About", name: "VizbiPlus About", component: () => import("../views/Plus/About.vue")},
      {
        path: "People",
        name: "VizbiPlus People",
        component: () => import("../views/Plus/People.vue")
      },
      {
        path: "Challenge",
        name: "VizbiPlus Challenge",
        component: () => import("../views/Plus/Challenge.vue")
      },
      {
        path: "Timeline",
        name: "VizbiPlus Timeline",
        component: () => import("../views/Plus/Timeline.vue")
      },
      {
        path: "Contact",
        name: "VizbiPlus Contact",
        component: () => import("../views/Plus/Contact.vue")
      },
      {
        path: "Events",
        name: "VizbiPlus Events",
        component: () => import("../views/Plus/Events.vue")
      },
      {path: "Stories", redirect: "/2013/Showcase/Stories"},
      {path: "Program", redirect: "/2013/Showcase/Program"},
      {path: "Venue", redirect: "/2013/Showcase/Venue"},
      {path: "Lightning", redirect: "/2013/Showcase/Lightning"},
      {path: ":legacyPath(.*)", redirect: "/Plus"}
    ]
  },
  {
    path: "/Live",
    name: "Live",
    component: () => import("../views/Live.vue")
  },
  {
    path: "/Public",
    name: "Public",
    component: () => import("../views/Public.vue")
  },
  {
    path: "/Test",
    name: "Test",
    component: () => import("../views/Test.vue") // admins and test users only (the page checks)
  },
  {
    path: "/Terms",
    name: "Terms",
    component: () => import("../views/Terms.vue")
  },
  {
    path: "/Videos",
    name: "Videos",
    component: () => import("../views/Videos/index.vue"),
    children: [
      {
        path: "",
        name: "Videos_Gallery",
        component: () => import("../components/Video/Gallery.vue")
      },
      {
        path: "Unlock",
        name: "Videos_Unlock",
        component: () => import("../views/Videos/Unlock.vue")
      },
      {
        // the Masterclass speakers, not yet in the navigation (fullstack
        // claude/UNLOCK-MASTERCLASS-SPEC.md, 2026-10-31)
        path: "Unlock/Masterclass",
        name: "Videos_Unlock_Masterclass",
        component: () => import("../views/Videos/UnlockMasterclass.vue")
      },
      {
        path: ":year(20\\d\\d)",
        name: "Videos_Year",
        component: () => import("../components/Video/Gallery.vue")
        // children: [
        //   {
        //     path: ":session(\\w+)",
        //     name: "Videos_Year_Session",
        //     component: () => import("../components/Video/Gallery.vue")
        //   }
        // ]
      },
      {
        path: ":session([A-Z][a-zA-Z-]*)", // Tech-Track too
        name: "Videos_Session",
        component: () => import("../components/Video/Gallery.vue")
      },
      {
        // a film shown for a limited time, e.g. /Videos/The_Birds (Video/films.js; 2026-11-05):
        // before the speakers' paths, which it would match
        path: `:vimeoHash(${FILM_PATHS.join("|")})`,
        name: "Video_Film",
        component: () => import("../components/Video/Film.vue")
      },
      {
        // one of a speaker's talks, or a video with no speaker: /Videos/Seán_O'Donoghue/2024,
        // /Videos/Christian_Stolte/2025/Masterclass, /Videos/Showreel/2024 (Video/videoPaths.js;
        // views/Videos/index.vue shows the video)
        path: ":speaker([^/]+)/:talkYear(\\d{4})/:part([^/]+)?",
        name: "Videos_Speaker_Year",
        component: () => import("../components/Video/Gallery.vue")
      },
      {
        // a speaker's videos, e.g. /Videos/Seán_O'Donoghue (an underscore; hashes have none): the
        // gallery of them, or the video itself when there is one (views/Videos/index.vue)
        path: ":speaker([^/]+_[^/]+)",
        name: "Videos_Speaker",
        component: () => import("../components/Video/Gallery.vue")
      },
      {
        path: ":vimeoHash(\\w{8,11})",
        name: "Video",
        component: () => import("../components/Video/Bespoke.vue")
      }
    ]
  },
  {
    path: "/:year(201[0-9]|202\\d)",
    beforeEnter(to, from, next) {
      const year = to.path.match(/\/(\d\d\d\d)/)?.[1];
      if (!year) return next();
      if (year > history.latestYear && location.host.toLowerCase().includes("vizbi.org")) {
        log(`redirect future years to current year`);
        return next({path: `/${history.latestYear}`});
      }
      next();
    },
    name: "Year",
    component: () => import("../views/Year/Index.vue"),
    children: [
      {
        path: "",
        name: "AboutYear",
        component: () => import("../views/Year/About.vue")
      },
      {
        path: "People",
        name: "People",
        component: () => import("../views/Year/People.vue")
      },
      {
        // the slides' old public addresses, e.g. /2010/slides/Oliver_Kohlbacher.pdf: since
        // 2026-10-14 behind sign-in (scripts/update/slides.js), so an old link opens the speaker
        path: "slides/:file(.*)",
        redirect: (to) => ({
          path: `/${to.params.year}/People`,
          hash: `#${to.params.file
            .split("/")
            .at(-1)
            .replace(/\.pdf$/i, "")}`
        })
      },
      {
        path: "Program",
        name: "Program",
        component: () => import("../views/Year/Program.vue")
      },
      {
        path: "Contact",
        name: "Contact",
        component: () => import("../views/Year/Contact.vue")
      },
      {
        path: "VR",
        name: "VR",
        component: () => import("../views/Year/VR.vue")
      },
      {
        path: "Masterclass",
        name: "Masterclass",
        component: () => import("../views/Year/Masterclass.vue")
      },
      {
        path: "Showcase",
        name: "Showcase",
        component: () => import("../views/Year/Showcase.vue")
      },
      {
        path: "Showcase/:showcaseEvent",
        name: "ShowcaseEvent",
        component: () => import("../views/Year/ShowcaseEvent.vue")
      },
      // name='Poster' not 'Posters': short and distinct from vizbi.org/Posters
      {
        path: "Photos",
        name: "Photos_Year",
        component: () => import("../views/Year/Photos.vue")
      },
      {
        path: "Platform",
        name: "Platform",
        component: () => import("../views/Year/Platform.vue")
      },
      {
        path: "Workshop",
        name: "Workshop",
        component: () => import("../views/Year/Workshop.vue")
      },
      {
        path: "Sponsors",
        name: "Sponsors",
        component: () => import("../views/Year/Sponsors.vue")
      },
      // name='Poster' not 'Posters': short and distinct from vizbi.org/Posters. A router
      // redirect, not window.location (Seán, 2026-11-01): that reloaded the whole app, so the
      // year's page flashed before the gallery. The gallery offers a way back (useYearArrival)
      {
        path: "Posters",
        name: "Poster",
        redirect(to) {
          noteYearArrival(to.params.year);
          return {path: `/Posters/${to.params.year}`};
        }
      },
      {
        path: "Registration",
        name: "Registration",
        component: () => import("../views/Year/Registration.vue")
      },
      {
        path: "Registration/Form",
        name: "Form",
        beforeEnter: redirectWithoutForm,
        component: () => import("../views/Year/Form.vue")
      },
      {
        path: "Venue",
        name: "Venue",
        component: () => import("../views/Year/Venue.vue")
      },
      {
        path: "Test",
        name: "TestYear",
        component: () => import("../views/Year/Test.vue")
      },
      {
        path: "Live",
        name: "LiveYear",
        component: () => import("../views/Live.vue")
      },
      {
        path: "media-kit",
        redirect: () => {
          window.location.href =
            "https://drive.google.com/drive/folders/1nZ-7ltmjn1yrTrMNKZxCruxmDylX0bR1?usp=sharing";
        }
      }
    ]
  }
];

log("routes/index.js: ", routes[2]);

//component: () => import(/* webpackChunkName: "about" */ 'About.vue')
// scroll solution below from https://stackoverflow.com/q/54535838
// the /Videos gallery under any filter (a year, a session, a speaker, ?unlocked or ?locked): choosing or
// clearing a filter keeps the page where it is, without a jump to the top (2026-10-21, Seán)
const GALLERY_ROUTES = new Set([
  "Videos_Gallery",
  "Videos_Year",
  "Videos_Session",
  "Videos_Speaker"
]);
const getIsGalleryFilter = (to, from) => {
  return GALLERY_ROUTES.has(to.name) && GALLERY_ROUTES.has(from.name);
};
let galleryScrollY = 0; // where the page was when a filter was chosen

const MAX_GALLERY_FRAMES = 60; // about a second
const nextFrame = () => new Promise((resolve) => requestAnimationFrame(resolve));
const getMaxScroll = () => document.documentElement.scrollHeight - window.innerHeight;

// the saved position, once the gallery has rendered tall enough to hold it (two frames weren't
// always enough: a phone's Safari clamped 200 to 160, staging round 7, 2026-10-21)
async function getGalleryScroll() {
  const top = galleryScrollY;
  await nextFrame();
  for (let frame = 0; frame < MAX_GALLERY_FRAMES; frame++) {
    if (getMaxScroll() >= top) break;
    await nextFrame();
  }
  return {top};
}

const router = createRouter({
  history: createWebHistory(), // or createWebHashHistory if needed
  routes,
  scrollBehavior(to, from, savedPosition) {
    // a filter in the /Videos gallery: back to where the page was, once the new view has
    // rendered (it's briefly empty, which would clamp the scroll; 2026-10-21, Seán)
    if (getIsGalleryFilter(to, from)) return getGalleryScroll();
    // a poster's #details (PosterPage.vue) is its open header, not an anchor: on the same
    // page, stay where you are; a link straight to it opens at the top
    if (getPosterAddress(to) && to.hash === "#details") {
      return to.path === from.path ? false : {top: 0};
    }
    // #profile's tabs (#profile&events…) are a dialog's state, not anchors: stay where you are
    if (/^#profile/.test(to.hash)) return;
    // the one-pager's version switch (?version=, OnePager.vue): the same page, so it stays put
    if (to.meta.isStandalone && to.path === from.path) return false;
    // /live's Join the Meeting dialog opens by itself as a session goes live: a dialog, not an
    // anchor, so the page stays where it is (Seán, 2026-11-03: it scrolled to the session)
    if (/^#join-meeting/.test(to.hash)) return;
    // the year page's sidebar panels (#faq, #highlights…; useYearSidebar.js): stay where you are
    if (getIsSidebarHash(to.hash)) return;
    if (to.hash && to.hash.length > 60) {
      // Skip scroll behavior for deeply nested auth states
      return;
    }

    if (savedPosition) {
      return savedPosition;
    }
    if (getPosterAddress(to)) return {top: 0};

    if (to.hash) {
      // Clean the hash of trailing slashes (e.g., #/sign-in/ → #/sign-in)
      const rawHash = to.hash;
      const cleanHash = rawHash.replace(/\/+$/, "");

      // This avoids selector errors like "#sign-in/"
      const handleScrollToAnchor = (hash) => {
        try {
          const el = document.querySelector(hash);
          if (el) {
            el.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        } catch (e) {
          console.warn("Invalid selector in scrollBehavior:", hash);
        }
      };

      const timer = setInterval(() => {
        if (document.readyState === "complete") {
          clearInterval(timer);
          handleScrollToAnchor(cleanHash);
        }
      }, 300);

      return {
        selector: decodeURI(cleanHash),
        offset: {
          x: 0,
          y: 80
        }
      };
    }
  }
});

log("routes/index.js: router.getRoutes() = ", router.getRoutes());

// TODO: move to separate file
// code below ensures router changes always go to top of new page
// however, it doesn't seem to stop navigation to page sections ('#')
// https://renatello.com/vue-js-scroll-top/
// See also https://stackoverflow.com/q/69109169/how-to-navigate-to-a-specific-elements-id-using-vue-router -->
// #register… and #welcome-back… opened on the Frontend (e.g. vizbi.org/#welcome-back&jane@…, from
// an email): those dialogs are the fullstack's, so the same hash opens there; on staging and
// local the link carries the session (getUrlWithAuth)
const FULLSTACK_HASHES = /^#(register|welcome-back)(&|$)/;
router.beforeEach((to) => {
  if (!FULLSTACK_HASHES.test(to.hash)) return true;
  const base = getFullstackUrl() || "https://fullstack.vizbi.org";
  window.location.replace(getUrlWithAuth(`${base}/${to.hash}`));
  return false;
});

// an email link's address (#…&email=…; prefillEmail.js): kept for the tab and taken out of the
// address, the rest of the hash as it was (#Nov-18,Nov-19, #sign-in); after the fullstack's
// hashes above, which take it there with them (Seán, 2026-10-27)
// A magic link's #…&magic=<ticket> too (magicLink.js): signed out, it signs the person in.
router.beforeEach((to) => {
  const magic = takeHashMagic(to.hash);
  const taken = takeHashEmail(magic ? magic.hash : to.hash);
  if (!magic && !taken) return true;
  if (taken?.email && !window.Clerk?.user) storePrefillEmail(taken.email); // signed in: thrown away
  signInWithMagic(magic?.ticket);
  return {path: to.path, query: to.query, hash: taken ? taken.hash : magic.hash, replace: true};
});

// a dialog opening or closing (only the #… changing) keeps the page's ?…: a video's t, version
// and cc (Video/videoUrlState.js) were dropped by #sign-in. Read from the address bar, which
// that file updates without the router knowing (2026-10-21)
function getQueryKept(to, from) {
  const isHashOnly = to.path === from.path && to.hash !== from.hash;
  if (!isHashOnly || Object.keys(to.query).length || !window.location.search) return;
  const query = Object.fromEntries(new URLSearchParams(window.location.search));
  return {path: to.path, query, hash: to.hash};
}
router.beforeEach((to, from) => getQueryKept(to, from) ?? true);

// /Videos' access filter is one of ?unlocked and ?locked (2026-10-24, Seán), never both; the
// earlier ?access=free|unlocked|locked|purchase links redirect to them (the first one named wins)
const LEGACY_ACCESS = {
  free: "unlocked",
  unlocked: "unlocked",
  locked: "locked",
  purchase: "locked"
};
function getAccessRedirect(to) {
  const {query} = to;
  const isBoth = "locked" in query && "unlocked" in query;
  if (!to.path.startsWith("/Videos") || !("access" in query || isBoth)) return;
  const first = Object.keys(query).find((key) => ["access", "locked", "unlocked"].includes(key));
  const filter = first === "access" ? LEGACY_ACCESS[query.access] : first;
  const {access, locked, unlocked, ...rest} = query; // eslint-disable-line no-unused-vars
  return {path: to.path, hash: to.hash, query: filter ? {...rest, [filter]: null} : rest};
}
router.beforeEach((to) => getAccessRedirect(to) ?? true);
router.beforeEach((to, from, next) => {
  log(`router: global guard called`);
  const isSamePage = to.meta.isSamePageAcrossParams && to.name === from.name;
  // the year page's sidebar (#faq, #faq&<answer>, #highlights&…) on the same page: an answer
  // opened or closed, a panel chosen; the page stays where it is (closing the last FAQ answer
  // jumped to the top; Seán, 2026-10-22)
  const isSidebarChange = to.path === from.path && getIsSidebarHash(to.hash);
  // a poster opened from the gallery: the gallery's place recorded for Back (scrolling to the
  // top here would record 0); the poster's page opens at the top (scrollBehavior)
  const isPosterOpened = from.name === "Posters" && Boolean(getPosterAddress(to));
  const isPosterDetails = to.path === from.path && Boolean(getPosterAddress(to)); // #details
  // /live's Join the Meeting opening by itself, or closing: the page stays where it is (Seán,
  // 2026-11-03: it jumped to the top as a session went live)
  const isJoinDialog = to.path === from.path && /^#join-meeting/.test(`${to.hash}${from.hash}`);
  // the one-pager's version switch (?version=, OnePager.vue): the same page, staying put (Seán,
  // 2026-11-05: it jumped to the top)
  const isStandaloneQuery = to.meta.isStandalone && to.path === from.path;
  const isScrollKept =
    isSamePage ||
    isSidebarChange ||
    getIsGalleryFilter(to, from) ||
    isPosterOpened ||
    isPosterDetails ||
    isJoinDialog ||
    isStandaloneQuery;
  if (getIsGalleryFilter(to, from)) galleryScrollY = window.scrollY;
  if (!isScrollKept) window.scrollTo(0, 0);
  try {
    next();
    if (localStorage.getItem("reloaded")) {
      localStorage.removeItem("reloaded"); // remove flag if set
    }
  } catch (error) {
    console.error("Error loading next page");
    // The error may be caused by 'TheImageBug'
    // If so, a simple page reload seems to fix this
    // https://stackoverflow.com/q/65955732
    if (!localStorage.getItem("reloaded")) {
      console.error("Will try one reload (only)");
      localStorage.setItem("reloaded", true); // set flag
      location.reload();
    } else {
      console.error("The page has already been re-loaded");
      localStorage.removeItem("reloaded"); // remove flag if set
      console.error(`Error: ${error}`); // print error to console
    }
  }
});

router.afterEach(() => {
  // Make sure meta-pixel 'fbq' is defined (might not be in initial page load)
  if (typeof window.fbq !== "undefined") {
    window.fbq("track", "PageView"); // tells pixel to track SPA page transition
  }
});

log("router: created");

export default router;
